import i18next from "i18next";
import React, { useState, useEffect } from "react";
import { Cap, Level } from "../../utils/rbacCatalog";
import { Label, TextInput, Select } from "flowbite-react";
import { Spinner } from "../atoms/Spinner";
import { HiLink, HiCheck } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import PaymentModuleService, {
  PaymentRequestDTO,
  PaymentRequestResponseDTO,
  PaymentRequestCurrencyEnum,
  PlanRegistrationResponseDTO,
} from "../../services/paymentModule";
import { CustomButton } from "../atoms/CustomButton";
import CopyToClipboardButton from "../atoms/CopyToClipboardButton";
import { ModalShell } from "../atoms/ModalShell";
import { PlanTier, usePermissions } from "../../utils/permissions";
import UpgradeModal from "../modals/UpgradeModal";
import { PremiumBenefit } from "../atoms/UpgradePromptContent";
import { HiCurrencyDollar } from "react-icons/hi";

type PaymentMode = "one-time" | "recurring";

interface CreatePaymentRequestModalProps {
  show: boolean;
  onClose: () => void;
  userIdentification?: string;
  userName?: string;
  onSuccess?: (response: PaymentRequestResponseDTO) => void;
  existingPaymentRequest?: PaymentRequestResponseDTO;
}

export const CreatePaymentRequestModal: React.FC<
  CreatePaymentRequestModalProps
> = ({
  show,
  onClose,
  userIdentification,
  userName,
  onSuccess,
  existingPaymentRequest,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const hasPaymentRequestAccess = permissions.hasFeatureAccess(
    "PAYMENT_REQUEST_CREATION"
  );
  const canCreatePaymentLink = permissions.can(
    Cap.CREATE_PAYMENT_LINK,
    Level.WRITE
  );

  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<PaymentRequestCurrencyEnum>("USD");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdRequest, setCreatedRequest] =
    useState<PaymentRequestResponseDTO | null>(existingPaymentRequest || null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [paymentMode, setPaymentMode] = useState<PaymentMode>("one-time");
  const [recurringPlans, setRecurringPlans] = useState<
    PlanRegistrationResponseDTO[]
  >([]);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string>("");
  const [loadingPlans, setLoadingPlans] = useState(false);

  const paymentRequestBenefits: PremiumBenefit[] = [
    {
      id: "secure-payments",
      text: t("paymentRequest.upgrade.benefit1") as string,
    },
    {
      id: "instant-links",
      text: t("paymentRequest.upgrade.benefit2") as string,
    },
    {
      id: "track-payments",
      text: t("paymentRequest.upgrade.benefit3") as string,
    },
    {
      id: "multiple-currencies",
      text: t("paymentRequest.upgrade.benefit4") as string,
    },
  ];

  const resetForm = () => {
    setDescription("");
    setAmount("");
    setCurrency("USD");
    setCreatedRequest(null);
    setPaymentMode("one-time");
    setSelectedPlanCode("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (
      show &&
      paymentMode === "recurring" &&
      recurringPlans.length === 0 &&
      !loadingPlans
    ) {
      fetchRecurringPlans();
    }
  }, [show, paymentMode]);

  const fetchRecurringPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await PaymentModuleService.getRecurringPlans(
        customer_identification,
        user.entity_identification
      );
      const allPlans = response.data?.element || [];
      setRecurringPlans(allPlans.filter((p) => p.recurrency !== "UNIQUE"));
    } catch (err: any) {
      setRecurringPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const validateForm = (): boolean => {
    if (!description.trim()) {
      toast.error(t("paymentRequest.errors.descriptionRequired"));
      return false;
    }
    if (description.length < 2 || description.length > 200) {
      toast.error(t("paymentRequest.errors.descriptionLength"));
      return false;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      toast.error(t("paymentRequest.errors.amountRequired"));
      return false;
    }

    const amountInCents = Math.round(amountValue * 100);
    if (amountInCents < 100 || amountInCents > 1000000) {
      toast.error(t("paymentRequest.errors.amountRange"));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!canCreatePaymentLink) return;
    if (paymentMode === "one-time") {
      if (!validateForm()) return;
    } else {
      if (!selectedPlanCode) {
        toast.error(
          t("paymentModule.createPaymentRequestModal.selectRecurringPlanError")
        );
        return;
      }
    }

    try {
      setIsSubmitting(true);

      if (paymentMode === "one-time") {
        const amountInCents = Math.round(parseFloat(amount) * 100);

        const requestData: PaymentRequestDTO = {
          description: description.trim(),
          amount: amountInCents,
          currency: currency,
        };

        if (userIdentification) {
          requestData.user_identification = userIdentification;
        }

        const response = await PaymentModuleService.createPaymentRequest(
          customer_identification,
          user.entity_identification,
          requestData
        );

        if (response.data?.element) {
          setCreatedRequest(response.data.element);
          toast.success(t("paymentRequest.success.created"));
          onSuccess?.(response.data.element);
        }
      } else {
        const selectedPlan = recurringPlans.find(
          (p) => p.plan_code === selectedPlanCode
        );
        const recurringDescription = (selectedPlan?.description || "").trim();

        const response =
          await PaymentModuleService.createRecurringPaymentRequest(
            customer_identification,
            user.entity_identification,
            {
              plan_code: selectedPlanCode,
              description: recurringDescription,
              ...(userIdentification
                ? { user_identification: userIdentification }
                : {}),
            }
          );

        if (response.data?.element) {
          setCreatedRequest(response.data.element);
          toast.success(t("paymentRequest.success.created"));
          onSuccess?.(response.data.element);
        }
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || t("paymentRequest.errors.createFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatAmount = (amountInCents: number, currencyCode: string) => {
    return new Intl.NumberFormat(i18next.language || "en", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(amountInCents / 100);
  };

  useEffect(() => {
    if (existingPaymentRequest) {
      setCreatedRequest(existingPaymentRequest);
    } else {
      setCreatedRequest(null);
    }
  }, [existingPaymentRequest, show]);

  return (
    <>
      <ModalShell
        show={show}
        onClose={handleClose}
        size="lg"
        title={
          <span className="flex items-center gap-3 min-w-0">
            <HiLink className="text-blue-600 flex-shrink-0" size={24} />
            <span className="truncate">{t("paymentRequest.modal.title")}</span>
          </span>
        }
        subtitle={
          userIdentification && userName ? (
            <span className="block truncate" title={userName}>
              {userName}
            </span>
          ) : undefined
        }
        footer={
          !createdRequest ? (
            <>
              <CustomButton
                title={t("common.cancelText")}
                onClick={handleClose}
                variant="secondary"
                disabled={isSubmitting}
              />
              <CustomButton
                title={
                  isSubmitting
                    ? (t("common.creating") as string)
                    : (t("paymentRequest.form.submit") as string)
                }
                onClick={() => {
                  if (!canCreatePaymentLink) {
                    return;
                  }
                  if (!hasPaymentRequestAccess) {
                    setShowUpgradeModal(true);
                  } else {
                    handleSubmit();
                  }
                }}
                variant="primary"
                disabled={isSubmitting || !canCreatePaymentLink}
                isLoading={isSubmitting}
              />
            </>
          ) : (
            <CustomButton
              title={t("common.done")}
              onClick={handleClose}
              variant="primary"
            />
          )
        }
      >
        {!createdRequest ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              {userIdentification
                ? t("paymentRequest.modal.descriptionForUser")
                : t("paymentRequest.modal.description")}
            </p>

            {userIdentification && userName && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>{t("paymentRequest.modal.forUser")}:</strong>{" "}
                  <span className="block truncate" title={userName}>
                    {userName}
                  </span>
                </p>
              </div>
            )}

            <div>
              <Label
                value={
                  t(
                    "paymentModule.createPaymentRequestModal.paymentTypeLabel"
                  ) as string
                }
              />
              <div className="mt-1 flex gap-2">
                <CustomButton
                  size="sm"
                  variant={paymentMode === "one-time" ? "primary" : "secondary"}
                  className="flex-1"
                  onClick={() => setPaymentMode("one-time")}
                  title={t("paymentModule.createPaymentRequestModal.oneTime")}
                />
                <CustomButton
                  size="sm"
                  variant={
                    paymentMode === "recurring" ? "primary" : "secondary"
                  }
                  className="flex-1"
                  onClick={() => setPaymentMode("recurring")}
                  title={t("paymentModule.createPaymentRequestModal.recurring")}
                />
              </div>
            </div>

            {paymentMode === "one-time" && (
              <div>
                <Label
                  htmlFor="plan_name"
                  value={t("paymentRequest.form.description") as string}
                />
                <TextInput
                  id="description"
                  type="text"
                  placeholder={
                    t("paymentRequest.form.descriptionPlaceholder") as string
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={70}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {description.length}/70 {t("paymentRequest.form.characters")}
                </p>
              </div>
            )}

            {paymentMode === "one-time" ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="amount"
                    value={t("paymentRequest.form.amount") as string}
                  />
                  <TextInput
                    id="amount"
                    type="number"
                    placeholder="10.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="1"
                    max="10000"
                    step="0.01"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("paymentRequest.form.amountRange")}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("paymentRequest.form.includeTax")}
                  </p>
                </div>

                <div>
                  <Label
                    htmlFor="currency"
                    value={t("paymentRequest.form.currency") as string}
                  />
                  <Select
                    id="currency"
                    value={currency}
                    onChange={(e) =>
                      setCurrency(e.target.value as PaymentRequestCurrencyEnum)
                    }
                    required
                  >
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="CHF">CHF - Swiss Franc</option>
                  </Select>
                </div>
              </div>
            ) : (
              <div>
                <Label
                  htmlFor="recurring-plan"
                  value={
                    t(
                      "paymentModule.createPaymentRequestModal.recurringPlanLabel"
                    ) as string
                  }
                />
                {loadingPlans ? (
                  <div className="flex items-center gap-2 py-2">
                    <Spinner size="sm" />
                    <span className="text-sm text-gray-500">
                      {t(
                        "paymentModule.createPaymentRequestModal.loadingPlans"
                      )}
                    </span>
                  </div>
                ) : recurringPlans.length > 0 ? (
                  <Select
                    id="recurring-plan"
                    value={selectedPlanCode}
                    onChange={(e) => setSelectedPlanCode(e.target.value)}
                    required
                  >
                    <option value="">
                      {t(
                        "paymentModule.createPaymentRequestModal.selectPlanPlaceholder"
                      )}
                    </option>
                    {recurringPlans.map((plan) => (
                      <option key={plan.plan_code} value={plan.plan_code}>
                        {plan.description}
                        {" · "}
                        {new Intl.NumberFormat(i18next.language || "en", {
                          style: "currency",
                          currency: plan.currency,
                          minimumFractionDigits: 2,
                        }).format(plan.amount / 100)}
                        /
                        {plan.recurrency === "MONTHLY"
                          ? t(
                              "paymentModule.createPaymentRequestModal.perMonth"
                            )
                          : t(
                              "paymentModule.createPaymentRequestModal.perYear"
                            )}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1">
                    <p className="text-sm text-amber-700">
                      {t(
                        "paymentModule.createPaymentRequestModal.noPlansAvailable"
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <HiCheck className="text-green-600" size={20} />
                <h3 className="text-sm font-semibold text-green-800">
                  {t("paymentRequest.success.title")}
                </h3>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-500">
                    {t("paymentRequest.result.description")}
                  </span>
                  <p className="text-sm font-medium text-gray-900">
                    {createdRequest.description}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">
                    {t("paymentRequest.result.amount")}
                  </span>
                  <p className="text-sm font-medium text-gray-900">
                    {formatAmount(
                      createdRequest.amount,
                      createdRequest.currency
                    )}
                    {createdRequest.recurrency &&
                      createdRequest.recurrency !== "UNIQUE" && (
                        <span className="text-gray-500 font-normal">
                          {" "}
                          /{" "}
                          {createdRequest.recurrency === "MONTHLY"
                            ? t(
                                "paymentModule.createPaymentRequestModal.perMonth"
                              )
                            : t(
                                "paymentModule.createPaymentRequestModal.perYear"
                              )}
                        </span>
                      )}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label
                value={t("paymentRequest.success.description") as string}
              />
              <div className="flex gap-2">
                <TextInput
                  type="text"
                  value={createdRequest.payment_link}
                  readOnly
                  className="flex-1"
                />
                <CopyToClipboardButton
                  textToCopy={createdRequest.payment_link}
                  variant="secondary"
                  fullWidth={false}
                  label={t("common.copy")}
                />
              </div>
            </div>
          </div>
        )}
      </ModalShell>

      <UpgradeModal
        showModal={showUpgradeModal}
        setShowModal={setShowUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("paymentRequest.upgrade.title") as string}
        description={t("paymentRequest.upgrade.description") as string}
        benefits={paymentRequestBenefits}
        icon={HiCurrencyDollar}
        iconTheme="green"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
    </>
  );
};

export default CreatePaymentRequestModal;
